import React, { useState } from 'react';

import AuthorUnit from '@components/FeedItems/AuthorUnit';
import useCurrentUser from '@hooks/useCurrentUser';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, click } from '@tracking';
import { CommentUser } from '@utils/backend-api/comments';
import { FollowType } from '@utils/backend-api/follow';

import { TextButton } from '../../Button/variants';
import FollowButton from '../../FollowButton';
import MessageModal from '../../MessageModal';
import Separator from '../Separator';

interface CommentHeaderProps {
  user: CommentUser;
  isAnonymous: boolean;
}

const CommentHeader = ({ user, isAnonymous }: CommentHeaderProps) => {
  const [messageUser, setMessageUser] = useState(false);
  const { currentUser } = useCurrentUser();
  const {
    canInteractWithProfile,
    company_name: company,
    followStatus,
    hasMessageAccess,
    id,
    identityId,
    name,
    picture_url: picture,
    profile_enabled: profileEnabled,
    staff: isStaff,
    title_at_company: title,
    username,
  } = user;
  const { trackEvent } = useEventTracking();

  const handleCommentMessageClick = () => {
    setMessageUser(true);
    trackEvent(click({ clickTarget: ClickTarget.CommentMessage, contentId: id }));
  };

  const showSeparator =
    canInteractWithProfile && profileEnabled && hasMessageAccess && id != currentUser?.id;

  return (
    <>
      <div className="mb-16 mt-48 flex flex-col items-start justify-between sm:flex-row sm:items-center">
        <AuthorUnit author={{ company, isStaff, name, picture, profileEnabled, title, username }} />
        <div className="ml-72 mt-8 flex items-center sm:ml-0 sm:mt-0">
          {canInteractWithProfile &&
            profileEnabled &&
            id != currentUser?.id &&
            currentUser?.is_subscriber &&
            !isAnonymous && (
              <FollowButton
                followStatus={followStatus}
                followeeId={identityId}
                followType={FollowType.Identity}
                username={username}
                className="!min-w-full items-center border-none !bg-transparent !p-0 !font-suisse-intl !text-button-sm !font-normal !lowercase !text-grayscale-gray no-underline hover:!text-primary-magenta"
              />
            )}
          {showSeparator && <Separator className="mx-16 shrink-0" />}
          {hasMessageAccess && id != currentUser?.id && !isAnonymous && (
            <TextButton
              as="button"
              onClick={handleCommentMessageClick}
              className="m-0 p-0 font-suisse-intl text-button-sm text-grayscale-gray no-underline"
            >
              message
            </TextButton>
          )}
        </div>
      </div>
      {messageUser && <MessageModal user={user} onClose={() => setMessageUser(false)} />}
    </>
  );
};

export default CommentHeader;
